import { ODSCheckbox } from 'kfone-component-library';
import type {
  RtiCustomFieldToggle,
  RtiEmployeeDataFields,
  RtiEmployeeDataSectionKey,
  RtiFieldLeaf,
} from 'model/interfaces/rti-employee-data-fields';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RTI_FIELD_HINT_KEYS,
  getRtiFieldLabel,
  isCustomFieldValue,
  isRtiTwelveMonthCompensationFieldKey,
} from 'utilities/rti-employee-data-fields';
import { getOrderedRtiSectionEntries } from 'utilities/rti-field-section-order';

export type RtiEmployeeDataFieldsProps = {
  value: RtiEmployeeDataFields;
  onChange: (next: RtiEmployeeDataFields) => void;
  disabled?: boolean;
};

const SECTION_ORDER: RtiEmployeeDataSectionKey[] = [
  'employee_information',
  'job_information',
  'location',
  'compensation',
];

const SECTION_TITLE_KEYS: Record<RtiEmployeeDataSectionKey, string> = {
  employee_information: 'pe.settings.rti.employeeData.category.employeeInformation',
  job_information: 'pe.settings.rti.employeeData.category.jobInformation',
  location: 'pe.db.factor.Location',
  compensation: 'pe.settings.rti.employeeData.category.compensation',
};

function FieldRowBoolean(props: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
}) {
  const { id, label, checked, onChange, description } = props;
  return (
    <div className='flex flex-col gap-1'>
      <ODSCheckbox
        id={id}
        checkList={[
          {
            label,
            value: id,
            checked,
          },
        ]}
        onChange={(checkbox) => {
          onChange(checkbox?.checked ?? false);
        }}
      />
      {description ? (
        <p className='ml-8 font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#747373]'>
          {description}
        </p>
      ) : null}
    </div>
  );
}

/** 12-month LTI/STI: checkbox only (no column label editor). */
function FieldRowLabeledToggle(props: {
  id: string;
  fieldKey: string;
  section: RtiEmployeeDataSectionKey;
  data: RtiCustomFieldToggle;
  value: RtiEmployeeDataFields;
  onChange: (next: RtiEmployeeDataFields) => void;
}) {
  const { t } = useTranslation();
  const { id, fieldKey, section, data, value, onChange } = props;

  const setEnabled = useCallback(
    (checked: boolean) => {
      onChange({
        ...value,
        [section]: {
          ...value[section],
          [fieldKey]: { ...data, enabled: checked },
        },
      });
    },
    [data, fieldKey, onChange, section, value],
  );

  const checkboxLabel = getRtiFieldLabel(fieldKey, t);

  return (
    <FieldRowBoolean
      id={`${id}-en`}
      label={checkboxLabel}
      checked={data.enabled}
      onChange={setEnabled}
    />
  );
}

function FieldRowCustom(props: {
  id: string;
  fieldKey: string;
  data: RtiCustomFieldToggle;
  section: RtiEmployeeDataSectionKey;
  value: RtiEmployeeDataFields;
  onChange: (next: RtiEmployeeDataFields) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { id, fieldKey, data, section, value, onChange, disabled } = props;

  const checkboxLabel = getRtiFieldLabel(fieldKey, t);

  const patch = useCallback(
    (nextLeaf: RtiCustomFieldToggle) => {
      onChange({
        ...value,
        [section]: {
          ...value[section],
          [fieldKey]: nextLeaf,
        },
      });
    },
    [fieldKey, onChange, section, value],
  );

  const setEnabled = useCallback(
    (checked: boolean) => {
      patch({ ...data, enabled: checked });
    },
    [data, patch],
  );

  const setLabel = useCallback(
    (label: string) => {
      patch({ ...data, label });
    },
    [data, patch],
  );

  return (
    <div className='flex flex-col gap-2'>
      <FieldRowBoolean
        id={`${id}-en`}
        label={checkboxLabel}
        checked={data.enabled}
        onChange={setEnabled}
      />
      <input
        type='text'
        className='ml-8 w-full max-w-[220px] rounded border border-[#C5C7CA] px-2 py-1.5 font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-[#1D1D1D] placeholder:text-[#6C6F75] disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:opacity-60'
        value={data.label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={checkboxLabel}
        disabled={disabled || !data.enabled}
        aria-label={t('pe.settings.rti.employeeData.custom.labelAria')}
      />
    </div>
  );
}

function ColumnHeader({ children }: { children: ReactNode }) {
  return (
    <p className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal uppercase text-[#5D5C5C]'>
      {children}
    </p>
  );
}

export function RtiEmployeeDataFields({ value, onChange, disabled }: RtiEmployeeDataFieldsProps) {
  const { t } = useTranslation();

  const updateLeaf = useCallback(
    (section: RtiEmployeeDataSectionKey, fieldKey: string, leaf: RtiFieldLeaf) => {
      onChange({
        ...value,
        [section]: {
          ...value[section],
          [fieldKey]: leaf,
        },
      });
    },
    [onChange, value],
  );

  const renderSection = (section: RtiEmployeeDataSectionKey) => {
    const entries = getOrderedRtiSectionEntries(value[section] ?? {});

    return (
      <div className='flex min-w-0 flex-col gap-4' key={section}>
        <ColumnHeader>{t(SECTION_TITLE_KEYS[section])}</ColumnHeader>
        <div className='flex flex-col gap-4'>
          {entries.map(([fieldKey, leaf]) => {
            const rowId = `rti-edf-${section}-${fieldKey}`.replace(/[^a-zA-Z0-9_-]/g, '_');
            if (typeof leaf === 'boolean') {
              const booleanLabel = getRtiFieldLabel(fieldKey, t);
              const compensationHintKey =
                section === 'compensation' ? RTI_FIELD_HINT_KEYS[fieldKey] : undefined;
              return (
                <FieldRowBoolean
                  key={fieldKey}
                  id={rowId}
                  label={booleanLabel}
                  checked={leaf}
                  onChange={(checked) => updateLeaf(section, fieldKey, checked)}
                  description={compensationHintKey ? t(compensationHintKey) : undefined}
                />
              );
            }
            if (isCustomFieldValue(leaf)) {
              if (section === 'compensation' && isRtiTwelveMonthCompensationFieldKey(fieldKey)) {
                return (
                  <FieldRowLabeledToggle
                    key={fieldKey}
                    id={rowId}
                    fieldKey={fieldKey}
                    section={section}
                    data={leaf}
                    value={value}
                    onChange={onChange}
                  />
                );
              }
              return (
                <FieldRowCustom
                  key={fieldKey}
                  id={rowId}
                  fieldKey={fieldKey}
                  data={leaf}
                  section={section}
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };

  return (
    <section className='flex flex-col gap-4' aria-labelledby='rti-employee-data-fields-title'>
      <h3
        id='rti-employee-data-fields-title'
        className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-bold text-[#1D1D1D]'
      >
        {t('pe.settings.rti.employeeData.title')}
      </h3>
      <p className='-mt-2 font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#1D1D1D]'>
        {t('pe.settings.rti.employeeData.helper')}
      </p>

      <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
        <div className='grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-4'>{SECTION_ORDER.map(renderSection)}</div>
      </div>
    </section>
  );
}
